import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';

// Sign in three equal ways: Google, a one-time code emailed to you, or a password. Plus email
// verification (6-digit code) for a pending account. Email/password sign-up is closed in Neon Auth;
// access to data still depends on a user_access row, whatever the sign-in method.
export default function Login({ pendingEmail }) {
  const [mode, setMode] = useState(pendingEmail ? 'verify' : 'choose');  // choose | password | code | verify
  const [email, setEmail] = useState(pendingEmail || '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(pendingEmail ? `Enter the code we emailed to ${pendingEmail}.` : '');

  const reset = () => { setError(''); setSuccess(''); };
  const needEmail = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setError('Enter your email address first.'); return true; }
    return false;
  };

  const handlePassword = async () => {
    reset();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  };

  const sendCode = async () => {
    reset();
    if (needEmail()) return;
    setLoading(true);
    const { error } = await supabase.auth.sendSignInCode(email.trim().toLowerCase());
    setLoading(false);
    if (error) { setError(error.message); return; }
    setCode('');
    setMode('code');
    setSuccess(`If ${email.trim()} can sign in here, a 6-digit code is on its way. Check your inbox.`);
  };

  const handleCode = async () => {
    reset();
    if (!code.trim()) { setError('Enter the code from your email.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.signInWithCode(email.trim().toLowerCase(), code.trim());
    if (error) setError(error.message);
    setLoading(false);
  };

  const handleVerify = async () => {
    reset();
    if (!code.trim()) {
      setError('Enter the code from your email.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({ email, token: code.trim(), type: 'signup' });
    if (error) setError(error.message);
    else setSuccess('Email verified.');
    setLoading(false);
  };

  const handleResend = async () => {
    reset();
    setLoading(true);
    const { error } = await supabase.auth.resend({ email, type: 'signup' });
    if (error) setError(error.message);
    else setSuccess(`A new code was sent to ${email}.`);
    setLoading(false);
  };

  const handleGoogle = async () => {
    reset();
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } });
    if (error) setError(error.message);
  };

  if (mode === 'verify') {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Property Manager</h1>
          <p>Verify your email</p>

          {error && <div className="error-msg">{error}</div>}
          {success && <div className="success-msg">{success}</div>}

          <div className="form-group">
            <label>Verification code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="123456"
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
            />
          </div>

          <button className="btn btn-primary" onClick={handleVerify} disabled={loading}>
            {loading ? 'Please wait...' : 'Verify'}
          </button>

          <div className="login-toggle">
            Didn't get it? <button onClick={handleResend} disabled={loading}>Resend code</button>
          </div>
          <div className="login-toggle">
            <button onClick={() => supabase.auth.signOut()}>Use a different account</button>
          </div>
        </div>
      </div>
    );
  }

  const choiceStyle = { width: '100%', marginTop: 8 };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Property Manager</h1>
        <p>Sign in to your account</p>

        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        {mode === 'code' ? (
          <>
            <div className="form-group">
              <label>Code sent to {email.trim()}</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="123456"
                name="code"
                onKeyDown={e => e.key === 'Enter' && handleCode()}
              />
            </div>
            <button className="btn btn-primary" onClick={handleCode} disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Please wait...' : 'Sign In'}
            </button>
            <div className="login-toggle">
              Didn't get it? <button onClick={sendCode} disabled={loading}>Send a new code</button>
            </div>
            <div className="login-toggle">
              <button onClick={() => { reset(); setMode('choose'); }}>Other ways to sign in</button>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                name="email"
                onKeyDown={e => e.key === 'Enter' && mode === 'password' && handlePassword()}
              />
            </div>

            {mode === 'password' && (
              <>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    name="password"
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && handlePassword()}
                  />
                </div>
                <button className="btn btn-primary" onClick={handlePassword} disabled={loading} style={{ width: '100%' }}>
                  {loading ? 'Please wait...' : 'Sign In'}
                </button>
                <div className="login-toggle">
                  <button onClick={() => { reset(); setMode('choose'); }}>Other ways to sign in</button>
                </div>
              </>
            )}

            {mode === 'choose' && (
              <div className="login-choices">
                <button className="btn btn-secondary" onClick={handleGoogle} disabled={loading} style={choiceStyle}>
                  Continue with Google
                </button>
                <button className="btn btn-secondary" onClick={sendCode} disabled={loading} style={choiceStyle}>
                  {loading ? 'Sending...' : 'Email me a code'}
                </button>
                <button className="btn btn-secondary" onClick={() => { reset(); setMode('password'); }} disabled={loading} style={choiceStyle}>
                  Sign in with password
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
